import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody, Checkbox, Menu, MenuHandler, MenuItem, MenuList} from "@material-tailwind/react";
import Card from "../../../components/Card";
import DataTable from "../../../components/DataTable/DataTable";
import {API} from "../../../types/API";
import {DataTableRequest} from "../../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import TD from "../../../components/DataTable/TD";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {useUserContext} from "../../../types/contexts/UserContext";
import {useNavigate} from "react-router-dom";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import {useToastContext} from "../../../types/contexts/ToastContext";
import TableSkeleton from "../../../components/Skeletons/TableSkeleton";
import {ProvidersResponse} from "../../../types/Responses/ProvidersResponse";
import FilterDropdown from "../../../components/FilterDropdown";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import {setTitle} from "../../../hooks/setTitle";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import usePersistedDataTableRequest from "../../../hooks/usePersistedDataTableRequest";
import {useDispatch} from "react-redux";
import FilterChips, {FilterChipDefinition} from "../../../components/FilterChips";
import {setSidebarTitle} from "../../../reducers/sidebar";
import DropdownInput from "../../../components/DropdownInput";

type ProviderDataTableRequest = DataTableRequest & { direct?: number };

const Providers = () => {
    const {t} = useTranslation();
    const user = useUserContext();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const handleError = useErrorHandler();
    const toast = useToastContext();
    const [providers, setProviders] = useState<ProvidersResponse>();
    const [bulkSelected, setBulkSelected] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const defaultRequest = useMemo<ProviderDataTableRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 0
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<ProviderDataTableRequest>(defaultRequest);
    const direct = (dataTableRequest.direct ?? 0) as 0 | 1 | 2;

    const clearRequest = (request: DataTableRequest | object) => {
        const keys = Object.keys(request);
        if (keys.includes('direct') && typeof request['direct'] !== "undefined" && parseInt(request['direct']) === 0)
            delete request['direct'];
        return request;
    }

    const sendRequest = useCallback((request: DataTableRequest) => {
        setIsDataTableLoading(true);
        API.getProviders(clearRequest(request)).then((response: AxiosResponse) => {
            const providersResponse = response.data as ProvidersResponse;
            setProviders(providersResponse);
            setIsDataTableLoading(false);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_PROVIDERS}`, toast, handleError, setIsForbidden));
    }, [handleError, toast]);

    const onDataTableChanged = useCallback((request: DataTableRequest) => {
        const updatedRequest = {...request, direct};
        persistDataTableRequest(updatedRequest);
        request = updatedRequest;
        if (!request.search || request.search.length > 3) {
            sendRequest(!request.search ? {...request, search: ''} : request);
        }
    }, [persistDataTableRequest, sendRequest, direct]);

    const onDirectChanged = useCallback((s: string | number) => {
        const n = typeof s === "string" ? parseInt(s) : s;
        const request = {
            ...dataTableRequest,
            page: 1,
            direct: n === 1 || n === 2 || n === 0 ? n : 0
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [sendRequest, dataTableRequest, persistDataTableRequest]);

    const onSelectAll = useCallback(() => {
        const request = {
            ...dataTableRequest,
            page: 1,
            direct: 0
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [sendRequest, dataTableRequest, persistDataTableRequest]);

    useEffect(() => {
        setTitle(t('admin.providers.title'));
        dispatch(setSidebarTitle(t('admin.providers.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                const locationSearch = window.location.search.substr(1).split('&');
                if (locationSearch.length > 0) {
                    const newID = locationSearch[0]?.split('=');
                    if (newID?.length) {
                        const request = {...dataTableRequest, search: newID[1]};
                        persistDataTableRequest(request);
                        sendRequest(request);
                    }
                } else {
                    sendRequest(dataTableRequest);
                }
            }).catch()
        }
    }, [user, dispatch, setSidebarTitle]);

    const onClickSelectAll = useCallback(() => {
        if (!providers)
            return;
        if (selectAll) {
            setSelectAll(false);
            setBulkSelected([]);
        } else {
            setSelectAll(true);
            setBulkSelected(providers.data.map(d => d.id));
        }
    }, [selectAll, bulkSelected, providers]);

    const onCheck = useCallback((id: number) => {
        if (bulkSelected.includes(id)) {
            setSelectAll(false);
            setBulkSelected(bulkSelected.filter(d => d !== id));
        } else {
            setBulkSelected([...bulkSelected, id]);
        }
    }, [bulkSelected]);

    const onBulkActionClick = useCallback((action: string | number) => {
        const numberAction = parseInt(action as string);
        if (!providers)
            return;
        setIsDataTableLoading(true);
        API.setBulkDirectAction(selectAll, bulkSelected, numberAction === 1).then(() => {
            setSelectAll(false);
            setBulkSelected([]);
            if (selectAll) {
                toast?.show(t('admin.bulk_actions.updated_all', {type: t('admin.providers.title').toLowerCase()}), 'bg-teal-500');
                sendRequest(dataTableRequest);
            } else {
                toast?.show(t('admin.bulk_actions.updated', {
                    count: bulkSelected.length,
                    type: t('admin.providers.title').toLowerCase()
                }), 'bg-teal-500');
                const newProviders = [...providers.data].map((provider) => {
                    if (bulkSelected.includes(provider.id)) {
                        provider.direct_redirect = numberAction === 1
                    }
                    return provider;
                });
                setProviders({...providers, data: newProviders});
                setIsDataTableLoading(false);
            }
        }).catch(e => ErrorHandler(e, `patch${APILink.UPDATE_BULK_PROVIDERS}`, toast, handleError, setIsForbidden));
    }, [bulkSelected, selectAll, dataTableRequest, sendRequest, toast, providers]);

    const refresh = useCallback(() => {
        sendRequest({...dataTableRequest});
    }, [dataTableRequest]);

    const filterChipsItems = useMemo<FilterChipDefinition[]>(() => {
        if (!direct) {
            return [];
        }
        const value = direct === 1 ? t('admin.providers.table.redirected') : t('admin.providers.table.not_redirected');
        return [{
            key: `direct-${direct}`,
            label: `${t('admin.providers.table.direct_redirect')}: ${value}`,
            onRemove: () => onDirectChanged('0')
        }];
    }, [direct, t, onDirectChanged]);

    const filters = useMemo(() => {
        return (

            <DropdownInput
                withAllButton={true}
                name={t('admin.providers.table.direct_redirect')}
                selected={direct}
                onChange={onDirectChanged}
                onSelectAll={onSelectAll}
                list={[{id: 1, name: t('admin.providers.table.redirected')}, {
                    id: 2,
                    name: t('admin.providers.table.not_redirected')
                }]}
                menuClassName="w-full -mt-5"
            />
        )
    }, [direct, onDirectChanged, onSelectAll, t]);

    const extraFilters = useMemo(() => {
        return <>
            {bulkSelected.length > 0 ?
                <FilterDropdown list={[
                    {id: 1, name: 'Set REDIRECTED'},
                    {id: 2, name: 'Set NOT REDIRECTED'},
                ]} name={t('admin.bulk_actions.title')}
                                onChange={onBulkActionClick}
                                searchable={false}
                                withAllElement={false}
                                className="mb-2 lg:mb-0"/> : ''}
        </>
    }, [bulkSelected, onBulkActionClick, t]);

    const dataTable = useMemo(() => {
        if (!providers)
            return <TableSkeleton/>;

        return <DataTable {...dataTableRequest}
                          total_pages={providers.pages.total}
                          count={providers.count}
                          nodata={providers.data.length === 0}
                          inProgress={isDataTableLoading}
                          filters={filters}
                          fullWidthFilters={<FilterChips className="px-4 -mt-6 mb-6" items={filterChipsItems}/>}
                          extraFilters={extraFilters}
                          rightPanel={<Button className="bg-transparent text-black dark:text-white shadow-none px-0.5"
                                              onClick={() => refresh()}>
                              <FontAwesomeIcon
                                  icon={solid('refresh')}
                                  className="w-12 h-3"/>
                          </Button>}
                          onChange={onDataTableChanged}
                          headers={[
                              {name: '#', sortBy: 'id'},
                              {
                                  name: 'check_all',
                                  element: <Checkbox color={'teal'} crossOrigin={''} checked={selectAll}
                                                     onChange={onClickSelectAll}/>,
                                  className: 'text-center px-[0px!important]'
                              },
                              {name: t('admin.providers.table.name'), sortBy: 'name'},
                              {
                                  name: t('admin.providers.table.direct_redirect'),
                                  className: 'text-center',
                                  sortBy: 'direct_redirect'
                              },
                              {name: '', className: 'text-right'}
                          ]}>
            <tbody>
            {providers.data.map((provider, index) => {
                    return <tr key={index}>
                        <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                        <TD className="w-2 px-[0px!important]">
                            <Checkbox color={'teal'} crossOrigin={''} checked={bulkSelected.includes(provider.id)}
                                      onChange={() => onCheck(provider.id)}/>
                        </TD>
                        <TD>{provider.name}</TD>
                        <TD className="text-center">{provider.direct_redirect ? t('admin.providers.table.redirected') : t('admin.providers.table.not_redirected')}</TD>
                        <TD className="text-right">
                            <>
                                <Menu>
                                    <MenuHandler>
                                        <Button className="text-white bg-black mr-2 px-5 py-2"><FontAwesomeIcon
                                            icon={solid('gear')}/></Button>
                                    </MenuHandler>
                                    <MenuList className="dark:bg-blue-gray-900 dark:text-white dark:border-blue-gray-500">
                                        {user?.user.permissions.includes('change credentials inputs') &&
                                            <MenuItem
                                                onClick={() => navigate(`/app/admin/providers/${provider.id}/builder/credentials`)}>
                                                <FontAwesomeIcon icon={solid('lock-open')}
                                                                 className="mr-2 text-warning w-5"/>
                                                <span>{t('admin.providers.table.credentials')}</span>
                                            </MenuItem>}
                                        {user?.user.permissions.includes('change client inputs') &&
                                            <MenuItem
                                                onClick={() => navigate(`/app/admin/providers/${provider.id}/builder/client`)}>
                                                <FontAwesomeIcon icon={solid('keyboard')}
                                                                 className="mr-2 text-info w-5"/>
                                                <span>{t('admin.providers.table.client')}</span>
                                            </MenuItem>}

                                        {user?.user.permissions.includes('update provider') &&
                                            <hr className="my-3 dark:border-blue-gray-500 outline-none"/>}

                                        {user?.user.permissions.includes('update provider') &&
                                            <MenuItem onClick={() => navigate('/app/admin/providers/' + provider.id)}>
                                                <FontAwesomeIcon icon={solid("pen-to-square")} className="w-5 mr-2"/>
                                                <span>Edit</span>
                                            </MenuItem>
                                        }
                                    </MenuList>
                                </Menu>
                            </>
                        </TD>
                    </tr>
                }
            )}
            </tbody>
        </DataTable>
    }, [dataTableRequest, extraFilters, isDataTableLoading, providers, navigate, filters, filterChipsItems, onDataTableChanged, user?.user.permissions, selectAll, onClickSelectAll, onCheck]);

    return (
        <PageBlock>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {dataTable}
                    <Forbidden isForbidden={isForbidden}/>
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(Providers);
